// ProfilePage.jsx
import React, { useMemo, useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import styles from "./Results.module.css"; // preserve seu CSS existente
import { getArchetypeFromTraits } from "../../utils/archetypes27";
import { computeInsights } from "../../utils/profileHelpers";
import { buildPremiumPayload } from "../../services/reportBuilder";
import images from "../../assets/personality"; // ajuste caminho se necessário
import Button from "../../components/common/Button";
import PersonalityTraits from "../../components/Profile/PersonalityTraits";
import CareerSection from "../../components/Profile/CareerSection";
import GrowthSection from "../../components/Profile/GrowthSection";
import RelationshipsSection from "../../components/Profile/RelationshipsSection";
import MonthlyPlanSection from "../../components/Profile/MonthlyPlanSection";
import { UserContext } from "../../context/UserContext";
import CharacterSection from "../../components/Profile/CharacterSection";
import TemperamentSection from "../../components/Profile/TemperamentSection";
import MetaphorSection from "../../components/Profile/MetaphorSection";
import clanTemplatePt from "../../data/pt/temperament_templates.json";
import clanTemplateEn from "../../data/en/temperament_templates.json";

export default function Results() {
  const { i18n, t } = useTranslation("user");
  const clanTemplate = i18n.language === "pt" ? clanTemplatePt : clanTemplateEn;
  const navigate = useNavigate();
  const userData = useContext(UserContext);

  const userLocalData = useMemo(() => {
    try {
      const raw = localStorage.getItem("ibhava_last_quiz_payload");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed;
    } catch (e) {
      return null;
    }
  }, []);

  const canViewPremium = false;

  function wrapPremium(
    sectionJSX,
    {
      reason = t("profile.wrapPremiumTitle"),
      ctaText = t("profile.wrapPremiumText"),
      ctaHref = "/pricing",
    } = {}
  ) {
    if (canViewPremium) return sectionJSX;
    // se não tem permissões, retorna o conteúdo borrado com overlay
    return (
      <div style={{ position: "relative" }}>
        <div className={styles.blurredSection} aria-hidden="true">
          {sectionJSX}
        </div>

        <div className={styles.premiumOverlay}>
          <div style={{ padding: 18, textAlign: "center" }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>{reason}</div>
            <div style={{ marginBottom: 10, color: "#6b7280" }}>
              {/* Adquira o Relatório Premium para ver o conteúdo completo. */}
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <a className={styles.btnSecondary} href={ctaHref}>
                {ctaText}
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const normalizeTraits = (raw) => {
    if (!raw) return [50, 50, 50, 50, 50, 50, 50];
    if (Array.isArray(raw)) {
      const arr = raw.slice(0);
      const nums = arr.map((v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 50;
      });
      while (nums.length < 7) nums.push(50);
      if (nums.length > 7) nums.length = 7;
      return nums;
    }
    // object-like fallback: try numeric keys
    if (typeof raw === "object") {
      const keys = Object.keys(raw)
        .filter((k) => !isNaN(Number(k)))
        .sort((a, b) => a - b);
      if (keys.length > 0) {
        const arr = keys.map((k) => Number(raw[k]));
        return normalizeTraits(arr);
      }
      const maybe = [
        "emotionality",
        "activity",
        "sociability",
        "discipline",
        "mentalOpenness",
        "emotionalOpenness",
        "perspective",
      ]
        .map((k) => raw[k])
        .filter((v) => v !== undefined);
      if (maybe.length >= 3) return normalizeTraits(maybe);
    }
    // string fallback
    if (typeof raw === "string") {
      try {
        return normalizeTraits(JSON.parse(raw));
      } catch (e) {
        return [50, 50, 50, 50, 50, 50, 50];
      }
    }
    return [50, 50, 50, 50, 50, 50, 50];
  };

  const traits = useMemo(
    () => normalizeTraits(userLocalData?.personalityTraits),
    [userLocalData?.personalityTraits]
  );

  // derive archetype safely
  const derivedArchetype = useMemo(() => {
    try {
      const three = traits.slice(0, 3).map((v) => Number(v) || 0);
      if (three.every((n) => n === 0)) return null;
      return getArchetypeFromTraits(three, i18n.language);
    } catch (e) {
      console.warn("derive archetype failed", e);
      return null;
    }
  }, [traits]);

  const insights = useMemo(() => {
    try {
      return computeInsights(traits, derivedArchetype, i18n.language);
    } catch (e) {
      console.error("computeInsights failed", e);
      // fallback minimal
      return computeInsights(
        [50, 50, 50, 50, 50, 50, 50],
        derivedArchetype,
        "pt"
      );
    }
  }, [traits, derivedArchetype]);

  const premiumPayload = useMemo(() => {
    try {
      return buildPremiumPayload({
        traitsArray: traits,
        archetypeOverride: derivedArchetype.meta,
        user: { name: "Usuário", email: "", id: "" },
        lang: i18n.language,
      });
    } catch (e) {
      console.error("buildPremiumPayload failed", e);
      return null;
    }
  }, [traits, derivedArchetype, userLocalData]);

  // avatar/clan image
  const clanKey = (
    derivedArchetype?.meta?.clan ||
    derivedArchetype?.key ||
    "breeze"
  )
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-");
  const clanImg = images[clanKey] || null;

  // Example character (profile-like) to show as "exemplo de perfil parecido"
  // We use archetypeOverride vs insights.archetype description
  const exampleCharacter = derivedArchetype
    ? {
        label: derivedArchetype.label,
        description: derivedArchetype.description,
        image:
          derivedArchetype.image ||
          `/images/${derivedArchetype.key || clanKey}-character.jpg`,
      }
    : {
        label: "Perfil Exemplo",
        description:
          "Um perfil que se assemelha ao seu temperamento e funciona como referência prática.",
        image: "",
      };

  if (!userData) return navigate("/profile");
  if (!userLocalData) return navigate("/home");

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>{t("profile.title")}</h1>
          <p className={styles.subtitle}>{t("profile.subtitle")}</p>
        </div>

        {/* <div className={styles.headerActions}>
          <Button onClick={() => navigate("/personality-quiz")}>
            {t("profile:retake_quiz", "Refazer Quiz")}
          </Button>
        </div> */}
      </header>

      <main className={styles.main}>
        {/* If premium payload missing show hint */}
        {!premiumPayload && (
          <section className={styles.card}>
            <p className={styles.muted}>{t("profile.alertNoPayload")}</p>
          </section>
        )}

        {premiumPayload && (
          <article id="premium-report" className={styles.premiumReport}>
            {/* Sessão 1 - Capa (inline) */}
            <section className={styles.cardCover}>
              <div className={styles.coverInner}>
                <div className={styles.coverLeft}>
                  {clanImg ? (
                    <img
                      src={clanImg}
                      alt={
                        clanTemplate?.clans[clanKey]?.label ||
                        derivedArchetype?.meta?.label ||
                        "Clã"
                      }
                      className={styles.coverImage}
                    />
                  ) : (
                    <div className={styles.coverPlaceholder}>
                      {derivedArchetype?.meta?.clan?.emoji || "🧭"}
                    </div>
                  )}
                </div>

                <div className={styles.coverRight}>
                  <h2 className={styles.clanTitle}>
                    {clanTemplate?.clans[clanKey]?.label || "Clã"}
                  </h2>
                  <p className={styles.clanSubtitle}>
                    "
                    {clanTemplate?.clans[clanKey]?.essence ||
                      "Seu clã — resumo."}
                    "
                  </p>
                  <strong>{t("profile.features")}</strong>
                  <p className={styles.clanSubtitle}>
                    {clanTemplate?.clans[clanKey]?.characteristics ||
                      "Seu clã — resumo."}
                  </p>
                  <strong>{t("profile.psychology")}</strong>
                  <p className={styles.clanSubtitle}>
                    {clanTemplate?.clans[clanKey]?.psychology ||
                      "Seu clã — resumo."}
                  </p>
                  <strong>{t("profile.qualities")}</strong>
                  <p className={styles.clanSubtitle}>
                    {clanTemplate?.clans[clanKey]?.qualities ||
                      "Seu clã — resumo."}
                  </p>
                  <strong>{t("profile.challenges")}</strong>
                  <p className={styles.clanSubtitle}>
                    {clanTemplate?.clans[clanKey]?.challenges ||
                      "Seu clã — resumo."}
                  </p>
                </div>
              </div>
            </section>

            {/* Sessão 2 - Processos da Personalidade */}
            <section className={styles.reportSection}>
              <section className={styles.card}>
                <h3>{t("profile.personalityProcesses")}</h3>
                <PersonalityTraits payload={premiumPayload} />
              </section>
            </section>

            {/* Sessão 3 - Perfil de Temperamento */}
            <section className={styles.reportSection}>
              <TemperamentSection
                canViewPremium={canViewPremium}
                derivedArchetype={derivedArchetype}
              />
            </section>

            {/* Sessão 4 - Perfil de Carácter */}
            <section className={styles.reportSection}>
              <CharacterSection premiumPayload={premiumPayload} />
            </section>

            {/* Sessão 5 - Metáfora */}
            <section className={styles.reportSection}>
              <MetaphorSection
                insights={insights}
                premiumPayload={premiumPayload}
                archetypeKey={"navigator"}
                // archetypeKey={premiumPayload?.archetypeKey}
                canViewPremium={canViewPremium}
                exampleCharacter={derivedArchetype}
                comicsBasePath="./comics/pt"
              />
            </section>

            {/* Sessão 6 - Carreira */}
            <section className={styles.reportSection}>
              <CareerSection
                premiumPayload={premiumPayload}
                insights={insights}
                derivedArchetype={derivedArchetype.meta}
                wrapPremium={wrapPremium}
              />
            </section>

            {/* Sessão 7 - Crescimento pessoal */}
            <section className={styles.reportSection}>
              <GrowthSection
                premiumPayload={premiumPayload}
                insights={insights}
                derivedArchetype={derivedArchetype.meta}
                wrapPremium={wrapPremium}
              />
            </section>

            {/* Sessão 8 - Relacionamentos */}
            <section className={styles.reportSection}>
              <RelationshipsSection
                premiumPayload={premiumPayload}
                insights={insights}
                derivedArchetype={derivedArchetype.meta}
                wrapPremium={wrapPremium}
              />
            </section>

            {/* Sessão 9 - Planejamento Mensal Personalizado (processos ACT aplicados) */}
            <section className={styles.reportSection}>
              <MonthlyPlanSection
                premiumPayload={premiumPayload}
                insights={insights}
                derivedArchetype={derivedArchetype.meta}
                wrapPremium={wrapPremium}
              />
            </section>

            {/* Sessão 8 - Tracker, Checklist e Próximos Passos */}
            {/* <section className={styles.reportSection}>
              <TrackerChecklist
                premiumPayload={premiumPayload}
                startDate={"2025-12-01"}
              />
            </section> */}

            {/* Footer of report */}
            <footer
              style={{
                marginTop: 22,
                paddingTop: 12,
                borderTop: "1px dashed #e6e9ef",
                color: "#6b7280",
                fontSize: 13,
              }}
            >
              {t("profile.footer")}{" "}
              {new Date().toLocaleDateString(i18n.language)}
            </footer>
          </article>
        )}

        {/* Small disclaimer */}
        <section className={styles.cardSmall}>
          <small className={styles.muted}>{t("profile.disclaimer")}</small>
        </section>
      </main>
    </div>
  );
}
